import { useState, useEffect, useCallback } from 'react';
import { IndexedDBService } from '../services/storage/IndexedDBService';
import { ledgerApi } from '../services/api/endpoints/ledger';
import { roundMoney, sumMoney, moneyEqual } from '../utils/currency';
import type { GeneralLedgerResponse, LedgerSearchResult, AccountMovementsResponse } from '../services/api/endpoints/ledger';
import { useFiscalYearStore } from '../stores/fiscalYearStore';
import { useBusinessUnitStore } from '../stores/businessUnitStore';
import type { Account, JournalEntry } from '../types/accounting';
import type { 
  LedgerFilters, 
  AccountMovementSearchParams, 
  TrialBalanceParams,
  TrialBalanceEntry,
  TrialBalanceResponse
} from '../types/filters';

export function useLedger() {
  const [trialBalance, setTrialBalance] = useState<TrialBalanceEntry[]>([]);
  const [trialBalanceResponse, setTrialBalanceResponse] = useState<TrialBalanceResponse | null>(null);
  const [accountMovements, setAccountMovements] = useState<Record<string, JournalEntry[]>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 50,
    total: 0,
    totalPages: 0
  });
  const [filters, setFilters] = useState<LedgerFilters>({});
  const { currentFiscalYear } = useFiscalYearStore();
  const { selectedBusinessUnit, effectiveFilter } = useBusinessUnitStore();

  // Recharger quand l'exercice comptable ou l'unité commerciale change
  useEffect(() => {
    // ⚡ Vider les données de l'ancien exercice avant rechargement
    setTrialBalance([]);
    setTrialBalanceResponse(null);
    setAccountMovements({});
    loadTrialBalance();
  }, [currentFiscalYear?.id, selectedBusinessUnit?.id]);

  /**
   * Mappe une entrée de balance de l'API vers le format interne
   * L'API retourne: { accountId, accountCode, accountName, accountType, debit, credit, balance: number, class }
   * Le frontend attend: { account: { id, code, name, type, class }, debit, credit, balance: { raw, ... } }
   */
  const mapApiTrialBalanceEntry = (apiEntry: any): TrialBalanceEntry => {
    const debit = Number(apiEntry.debit) || 0;
    const credit = Number(apiEntry.credit) || 0;
    const rawBalance = typeof apiEntry.balance === 'number' 
      ? apiEntry.balance 
      : roundMoney(debit - credit);
    
    return {
      account: {
        id: apiEntry.accountId || apiEntry.id || '',
        code: apiEntry.accountCode || apiEntry.code || '',
        name: apiEntry.accountName || apiEntry.name || '',
        type: apiEntry.accountType || apiEntry.type || 'asset',
        class: apiEntry.class || apiEntry.accountCode?.charAt(0) || ''
      },
      debit,
      credit,
      balance: {
        raw: rawBalance,
        debitBalance: rawBalance > 0.005 ? rawBalance : 0,
        creditBalance: rawBalance < -0.005 ? Math.abs(rawBalance) : 0,
        normalized: rawBalance
      }
    };
  };

  const loadTrialBalance = async (params?: TrialBalanceParams) => {
    try {
      setLoading(true);
      setError(null);
      const fiscalYearId = params?.fiscalYearId || currentFiscalYear?.id;
      
      // Try API first if online
      if (navigator.onLine) {
        try {
          const response = await ledgerApi.getTrialBalance({ 
            ...params, 
            fiscalYearId,
            // Ajouter les filtres d'unité commerciale
            businessUnitId: effectiveFilter.businessUnitId,
            businessUnitType: effectiveFilter.businessUnitType
          });
          if (response.success && response.data) {
            // Stocker la réponse complète pour les contrôles
            setTrialBalanceResponse(response.data);
            
            // Extraire les entrées - l'API retourne 'entries' ou 'items'
            const rawApiData = response.data.entries || response.data.items || [];
            
            // Mapper les données de l'API vers le format frontend
            const mappedData: TrialBalanceEntry[] = Array.isArray(rawApiData) 
              ? rawApiData.map(mapApiTrialBalanceEntry)
              : [];
            
            setTrialBalance(mappedData);
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error loading trial balance from API:', error);
        }
      }

      // Fallback to local calculation from IndexedDB only
      const localAccounts = await IndexedDBService.getAll<Account>('accounts');
      const localEntries = await IndexedDBService.getAll<JournalEntry>('journal_entries');

      // Pas de données mockées en production - utiliser uniquement les données locales
      if (localAccounts.length === 0) {
        console.log('📭 Aucun compte disponible localement');
        setTrialBalance([]);
        setLoading(false);
        return;
      }
      
      const accounts = localAccounts;
      let entries = localEntries;

      // Filtrer par exercice comptable si défini
      if (fiscalYearId) {
        entries = entries.filter(entry => entry.fiscalYearId === fiscalYearId);
      }
      
      // Filtrer par unité commerciale si définie (côté client)
      if (effectiveFilter.businessUnitId) {
        entries = entries.filter(entry => 
          (entry as any).businessUnitId === effectiveFilter.businessUnitId ||
          effectiveFilter.businessUnitIds?.includes((entry as any).businessUnitId)
        );
      }

      // Calculer la balance avec filtres
      let filteredAccounts = [...accounts];
      
      if (params?.date) {
        // Filtrer les entrées jusqu'à la date spécifiée
        const filterDate = new Date(params.date);
        entries = entries.filter(entry => new Date(entry.date) <= filterDate);
      }

      const balance: TrialBalanceEntry[] = filteredAccounts.map(account => {
        const accountEntries = entries.filter(entry =>
          entry.lines.some(line => line.accountId === account.id)
        );

        const totals = accountEntries.reduce((acc, entry) => {
          entry.lines.forEach(line => {
            if (line.accountId === account.id) {
              acc.debit += Number(line.debit) || 0;
              acc.credit += Number(line.credit) || 0;
            }
          });
          return acc;
        }, { debit: 0, credit: 0 });

        const rawBalance = roundMoney(totals.debit - totals.credit);
        
        return {
          account: {
            id: account.id,
            code: account.code,
            name: account.name,
            type: account.type,
            class: account.class
          },
          debit: roundMoney(totals.debit),
          credit: roundMoney(totals.credit),
          balance: {
            raw: rawBalance,
            debitBalance: rawBalance > 0.005 ? rawBalance : 0,
            creditBalance: rawBalance < -0.005 ? Math.abs(rawBalance) : 0,
            normalized: calculateNormalizedBalance(account.type, totals.debit, totals.credit)
          }
        };
      });

      setTrialBalance(balance);
      
      // Créer une réponse locale pour les contrôles
      const totalDebit = sumMoney(balance.map(item => item.debit));
      const totalCredit = sumMoney(balance.map(item => item.credit));
      
      setTrialBalanceResponse({
        period: {
          asOfDate: params?.date || new Date().toISOString().split('T')[0],
          fiscalYear: fiscalYearId || ''
        },
        companyId: '',
        items: balance,
        totals: {
          totalDebit,
          totalCredit,
          totalDebitBalance: sumMoney(balance.map(item => item.balance.debitBalance)),
          totalCreditBalance: sumMoney(balance.map(item => item.balance.creditBalance))
        },
        controls: {
          isBalanced: moneyEqual(totalDebit, totalCredit),
          balancesMatch: true,
          difference: roundMoney(totalDebit - totalCredit)
        },
        accountCount: balance.length,
        generatedAt: new Date().toISOString()
      });
    } catch (err) {
      console.error('Error loading trial balance:', err);
      setError('Impossible de charger la balance de vérification');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Calcule le solde normalisé selon la nature du compte
   * - Actifs/Charges: solde débiteur normal (débit - crédit)
   * - Passifs/Capitaux/Produits: solde créditeur normal (crédit - débit)
   */
  const calculateNormalizedBalance = (
    type: 'asset' | 'liability' | 'equity' | 'revenue' | 'expense',
    debit: number,
    credit: number
  ): number => {
    switch (type) {
      case 'asset':
      case 'expense':
        return debit - credit;
      case 'liability':
      case 'equity':
      case 'revenue':
        return credit - debit;
      default:
        return debit - credit;
    }
  };

  const loadAccountMovements = async (accountId: string, params?: AccountMovementSearchParams) => {
    if (!accountId) {
      return; // Skip if no account selected
    }
    
    try {
      setLoading(true);
      const fiscalYearId = params?.fiscalYearId || currentFiscalYear?.id;

      // Try API first if online
      if (navigator.onLine) {
        try {
          const response = await ledgerApi.getAccountMovements(accountId, { 
            ...params, 
            fiscalYearId,
            businessUnitId: effectiveFilter.businessUnitId,
            businessUnitType: effectiveFilter.businessUnitType
          });
          
          if (response.success && response.data) {
            // La réponse du backend peut avoir les mouvements dans:
            // - response.data.movements (format attendu par l'interface)
            // - response.data.data (format réel retourné par le backend)
            const rawData = response.data as any;
            let movements = rawData.movements || rawData.data || [];
            
            const totalPages = rawData.totalPages || 1;
            const currentPage = rawData.page || 1;
            
            // Si plusieurs pages, charger toutes les pages pour avoir toutes les données
            // Le DataTable fait la pagination côté client
            if (totalPages > 1 && currentPage === 1) {
              const allMovements = [...movements];
              
              // Charger les pages restantes
              for (let page = 2; page <= totalPages; page++) {
                try {
                  const nextPageResponse = await ledgerApi.getAccountMovements(accountId, { 
                    ...params, 
                    fiscalYearId,
                    businessUnitId: effectiveFilter.businessUnitId,
                    businessUnitType: effectiveFilter.businessUnitType,
                    page,
                    pageSize: rawData.pageSize || 50
                  });
                  
                  if (nextPageResponse.success && nextPageResponse.data) {
                    const nextRawData = nextPageResponse.data as any;
                    const nextMovements = nextRawData.movements || nextRawData.data || [];
                    if (Array.isArray(nextMovements)) {
                      allMovements.push(...nextMovements);
                    }
                  }
                } catch (pageError) {
                  console.error(`Error loading page ${page} of account movements:`, pageError);
                  // Continuer avec les données déjà chargées
                }
              }
              
              movements = allMovements;
            }
            
            // Fonction helper pour formater un mouvement en JournalEntry
            const formatMovement = (movement: any): JournalEntry => {
              // Extraire debit/credit depuis 'line' si présent, sinon depuis le mouvement directement
              const line = movement.line || {};
              const debit = parseFloat(String(line.debit || movement.debit || 0));
              const credit = parseFloat(String(line.credit || movement.credit || 0));
              
              return {
                id: movement.id,
                date: movement.date,
                journalType: movement.journalType,
                reference: movement.reference,
                description: movement.description || line.description || '',
                status: (movement.status || 'posted') as 'draft' | 'pending' | 'posted' | 'rejected',
                lines: [
                  {
                    accountId,
                    accountCode: line.accountCode,
                    accountName: line.accountName,
                    debit: isNaN(debit) ? 0 : debit,
                    credit: isNaN(credit) ? 0 : credit
                  }
                ],
                fiscalYearId: fiscalYearId || ''
              };
            };
            
            // Convertir les mouvements en format JournalEntry-like pour l'affichage
            // Le backend retourne les valeurs dans 'line' avec debit/credit en string
            const formattedMovements: JournalEntry[] = (Array.isArray(movements) ? movements : []).map(formatMovement);
            
            setAccountMovements(prev => ({
              ...prev,
              [accountId]: formattedMovements
            }));
            
            // Pagination - toutes les données sont chargées côté client
            setPagination({
              page: 1,
              pageSize: rawData.total || movements.length,
              total: rawData.total || movements.length,
              totalPages: 1 // Toutes les données sont chargées
            });
            setLoading(false);
            return;
          }
        } catch (error) {
          console.error('Error loading account movements from API:', error);
        }
      }

      // Fallback to local data only - no mock data in production
      const localEntries = await IndexedDBService.getAll<JournalEntry>('journal_entries');
      
      // Si pas de données locales, retourner une liste vide
      if (localEntries.length === 0) {
        console.log('📭 Aucun mouvement disponible localement pour ce compte');
        setAccountMovements(prev => ({
          ...prev,
          [accountId]: []
        }));
        setPagination({
          page: 1,
          pageSize: 50,
          total: 0,
          totalPages: 0
        });
        setLoading(false);
        return;
      }
      
      let entries = localEntries;
      
      // Filtrer par exercice comptable si défini
      if (fiscalYearId) {
        entries = entries.filter(entry => entry.fiscalYearId === fiscalYearId);
      }
      
      let filteredMovements = entries
        .filter(entry => entry.lines.some(line => line.accountId === accountId));

      // Apply filters
      if (params?.startDate) {
        filteredMovements = filteredMovements.filter(entry => 
          entry.date >= params.startDate!
        );
      }
      
      if (params?.endDate) {
        filteredMovements = filteredMovements.filter(entry => 
          entry.date <= params.endDate!
        );
      }
      
      if (params?.journalType && params.journalType !== 'all') {
        filteredMovements = filteredMovements.filter(entry => 
          entry.journalType === params.journalType
        );
      }
      
      if (params?.status && params.status !== 'all') {
        filteredMovements = filteredMovements.filter(entry => 
          entry.status === params.status
        );
      }

      // Sorting
      if (params?.sortBy) {
        filteredMovements.sort((a, b) => {
          const order = params.sortOrder === 'desc' ? -1 : 1;
          if (params.sortBy === 'date') {
            return (new Date(a.date).getTime() - new Date(b.date).getTime()) * order;
          } else if (params.sortBy === 'amount') {
            const aAmount = a.totalDebit || a.totalCredit;
            const bAmount = b.totalDebit || b.totalCredit;
            return (aAmount - bAmount) * order;
          } else if (params.sortBy === 'reference') {
            return a.reference.localeCompare(b.reference) * order;
          }
          return 0;
        });
      }

      // Pagination
      const page = params?.page || 1;
      const pageSize = params?.pageSize || 50;
      const total = filteredMovements.length;
      const totalPages = Math.ceil(total / pageSize);
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize;
      
      setAccountMovements(prev => ({
        ...prev,
        [accountId]: filteredMovements.slice(startIndex, endIndex)
      }));
      
      setPagination({
        page,
        pageSize,
        total,
        totalPages
      });
    } catch (error) {
      console.error('Error loading account movements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAccountBalance = async (accountId: string, date?: string) => {
    try {
      if (navigator.onLine) {
        const response = await ledgerApi.getAccountBalance(accountId, { date });
        if (response.success && response.data) {
          return response.data;
        }
      }

      // Fallback to local calculation only - no mock data in production
      const localEntries = await IndexedDBService.getAll<JournalEntry>('journal_entries');
      
      // Si pas de données locales, retourner des valeurs à zéro
      if (localEntries.length === 0) {
        console.log('📭 Aucune écriture disponible localement pour calculer le solde');
        return {
          accountId,
          debit: 0,
          credit: 0,
          balance: 0
        };
      }
      
      let relevantEntries = localEntries.filter(entry =>
        entry.lines.some(line => line.accountId === accountId)
      );

      if (date) {
        const filterDate = new Date(date);
        relevantEntries = relevantEntries.filter(entry => 
          new Date(entry.date) <= filterDate
        );
      }

      const totals = relevantEntries.reduce((acc, entry) => {
        entry.lines.forEach(line => {
          if (line.accountId === accountId) {
            acc.debit += line.debit || 0;
            acc.credit += line.credit || 0;
          }
        });
        return acc;
      }, { debit: 0, credit: 0 });

      return {
        accountId,
        debit: totals.debit,
        credit: totals.credit,
        balance: totals.debit - totals.credit
      };
    } catch (error) {
      console.error('Error getting account balance:', error);
      return {
        accountId,
        debit: 0,
        credit: 0,
        balance: 0
      };
    }
  };

  const applyFilters = useCallback((newFilters: LedgerFilters) => {
    setFilters(newFilters);
    const params: TrialBalanceParams = {
      date: newFilters.startDate,
      mode: newFilters.mode,
      currency: newFilters.currency,
      includeZeroBalances: true,
      groupByClass: false
    };
    loadTrialBalance(params);
  }, []);

  /**
   * Recherche dans le grand livre
   */
  const searchLedger = async (query: string, params?: LedgerFilters) => {
    try {
      setLoading(true);
      if (navigator.onLine) {
        const response = await ledgerApi.search(query, {
          ...params,
          fiscalYearId: params?.fiscalYearId || currentFiscalYear?.id,
          businessUnitId: effectiveFilter.businessUnitId,
          businessUnitType: effectiveFilter.businessUnitType
        });
        if (response.success && response.data) {
          return response.data;
        }
      }
      return null;
    } catch (error) {
      console.error('Error searching ledger:', error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Exporter le grand livre ou la balance
   */
  const exportLedger = async (format: 'pdf' | 'excel' | 'csv', exportType: 'trial-balance' | 'account-movements' = 'trial-balance', accountId?: string) => {
    try {
      setLoading(true);
      const response = await ledgerApi.exportLedger({
        format,
        exportType,
        accountId,
        fiscalYearId: currentFiscalYear?.id,
        businessUnitId: effectiveFilter.businessUnitId,
        businessUnitType: effectiveFilter.businessUnitType
      });
      return response.data;
    } catch (error) {
      console.error('Error exporting ledger:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  return {
    trialBalance,
    trialBalanceResponse,     // Réponse complète avec contrôles
    accountMovements,
    loading,
    error,
    pagination,
    filters,
    loadTrialBalance,
    loadAccountMovements,
    getAccountBalance,
    applyFilters,
    searchLedger,             // Nouvelle méthode de recherche
    exportLedger,             // Nouvelle méthode d'export
    // Exposer les infos de filtre actuels
    currentFilters: {
      fiscalYearId: currentFiscalYear?.id,
      businessUnitId: selectedBusinessUnit?.id,
      businessUnitType: selectedBusinessUnit?.type
    }
  };
}
